/**
 * ProfitSpot Yield Explorer — The Core Application
 * Neon Noir themed DeFi Yield Intelligence Platform
 * v2: Premium Gating, Break-even Charts, Stripe Integration
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FeatureInfo } from '../components/FeatureInfo';
import ForecastDial from '../components/ForecastDial';
import { getBackendBaseUrl } from '../lib/apiBase';
import { 
  TrendingUp, Shield, AlertTriangle, 
  ChevronRight, Filter, RefreshCw, Info, Lock,
  DollarSign, Clock, Zap, Users, User, LogOut,
  ChevronDown, X, Menu, Sparkles, Crown, Check,
  Unlock, Eye, BarChart3, Search, ArrowUpDown,
  Share2, Copy, FlaskConical, Calculator, Brain,
  CheckCircle2, XCircle, Flame, Gauge, Bell, Fuel, Bitcoin,
  Send, Minimize2, ChevronLeft, FileText, Download, Loader2
} from 'lucide-react';

const API_URL = getBackendBaseUrl();

// ─── Utilities ───────────────────────────────────────────────
const CHAIN_COLORS = {
  ethereum: 'chain-ethereum', arbitrum: 'chain-arbitrum',
  polygon: 'chain-polygon', optimism: 'chain-optimism',
  base: 'chain-base', bsc: 'chain-bsc',
  avalanche: 'chain-avalanche', solana: 'chain-solana'
};

const formatUSD = (v) => {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
};

// ─── Break-even Area Chart (Lightweight SVG) ─────────────────
const BreakEvenChart = ({ data, crossoverDay }) => {
  if (!data || data.length === 0) return null;

  const width = 100;
  const height = 60;
  const padding = { top: 5, right: 5, bottom: 12, left: 8 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  // Calculate scales
  const maxY = Math.max(
    ...data.map(d => Math.max(d.cumulative_yield, d.total_costs))
  );
  const xScale = (i) => padding.left + (i / (data.length - 1)) * chartW;
  const yScale = (v) => padding.top + chartH - (v / maxY) * chartH;

  // Generate path data
  const yieldPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.cumulative_yield)}`).join(' ');
  const yieldAreaPath = `${yieldPath} L ${xScale(data.length - 1)} ${yScale(0)} L ${xScale(0)} ${yScale(0)} Z`;
  
  const costsY = yScale(data[0]?.total_costs || 0);
  const costsPath = `M ${padding.left} ${costsY} L ${padding.left + chartW} ${costsY}`;
  const costsAreaPath = `M ${padding.left} ${costsY} L ${padding.left + chartW} ${costsY} L ${padding.left + chartW} ${yScale(0)} L ${padding.left} ${yScale(0)} Z`;

  // Find crossover point
  const crossoverIndex = data.findIndex(d => d.break_even_reached);
  const crossoverX = crossoverIndex >= 0 ? xScale(crossoverIndex) : null;

  return (
    <div className="chart-container" data-testid="break-even-chart">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full">
        <defs>
          <linearGradient id="gradient-yield" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#00ff88" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#00ff88" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="gradient-costs" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#00d4ff" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1={padding.left} y1={padding.top + chartH * (1 - pct)} x2={padding.left + chartW} y2={padding.top + chartH * (1 - pct)} className="chart-grid-line" />
        ))}

        {/* Costs area (flat line) */}
        <path d={costsAreaPath} className="chart-area-costs" />
        <path d={costsPath} className="chart-line-costs" />

        {/* Yield area (growing) */}
        <path d={yieldAreaPath} className="chart-area-yield" />
        <path d={yieldPath} className="chart-line-yield" />

        {/* Crossover marker */}
        {crossoverX && (
          <>
            <line x1={crossoverX} y1={padding.top} x2={crossoverX} y2={padding.top + chartH} className="chart-crossover-line" />
            <circle cx={crossoverX} cy={costsY} r="2" className="chart-crossover-marker" />
          </>
        )}

        {/* X-axis labels */}
        <text x={padding.left} y={height - 2} className="chart-axis-label">Day 1</text>
        <text x={padding.left + chartW} y={height - 2} className="chart-axis-label" textAnchor="end">{data.length}d</text>
      </svg>

      <div className="chart-legend">
        <div className="chart-legend-item">
          <span className="chart-legend-dot costs"></span>
          <span>Initial Costs</span>
        </div>
        <div className="chart-legend-item">
          <span className="chart-legend-dot yield"></span>
          <span>Projected Yield</span>
        </div>
        {crossoverDay && (
          <div className="chart-legend-item">
            <span className="chart-legend-dot crossover"></span>
            <span>Time-to-Profit: {crossoverDay}d</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Premium Unlock Overlay ──────────────────────────────────
const PremiumUnlockOverlay = ({ feature, tier, onUnlock, onSignIn, isAuthenticated }) => (
  <div className="premium-unlock-overlay" data-testid="premium-unlock-overlay">
    <div className="unlock-card">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Crown className="w-6 h-6 text-[#ffa502]" />
        <span className="text-sm font-bold text-[#ffa502] uppercase tracking-wider">Premium</span>
      </div>
      <h3 className="text-lg font-bold text-white mb-2">{feature}</h3>
      <p className="text-xs text-gray-400 mb-4">
        Unlock {tier === 'elite' ? 'Elite' : 'Pro'} features to access this intelligence
      </p>
      {!isAuthenticated ? (
        <button onClick={onSignIn} className="w-full flex items-center justify-center gap-2 bg-[#00ff88]/20 text-[#00ff88] font-bold py-2.5 px-4 rounded-lg text-sm hover:bg-[#00ff88]/30 transition-colors" data-testid="unlock-sign-in-btn">
          <User className="w-4 h-4" /> Sign In First
        </button>
      ) : (
        <button onClick={onUnlock} className="w-full flex items-center justify-center gap-2 bg-[#00ff88] text-black font-bold py-2.5 px-4 rounded-lg text-sm hover:bg-[#00cc6a] transition-colors" data-testid="unlock-now-btn">
          <Unlock className="w-4 h-4" /> Unlock Now
        </button>
      )}
    </div>
  </div>
);

// ─── Subscription Modal ──────────────────────────────────────
const SubscriptionModal = ({ onClose, currentTier }) => {
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      id: 'pro_monthly',
      name: 'Pro',
      price: 29,
      popular: true,
      features: ['500 yield pools', 'Security scores', 'Time-to-Profit charts', 'Full Simulator Labs', 'Portfolio Tracker', '3 email alerts/day', '7-day free trial'],
      finePrint: 'Annual: $19/mo ($228/yr)'
    },
    {
      id: 'elite_monthly',
      name: 'Elite',
      price: 79,
      features: ['Unlimited pools', 'Whale Tracker', 'AI Pool Insights', 'Advanced Backtesting', 'Telegram + unlimited alerts', 'Smart Yield Alerts', 'CSV export'],
      finePrint: 'Annual: $59/mo ($708/yr)'
    }
  ];

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: planId, origin_url: originUrl, payment_method: 'card' })
      });
      
      if (!res.ok) throw new Error('Failed to create checkout');
      
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div className="subscription-modal-backdrop" data-testid="subscription-modal">
      <div className="bg-[#0f0f1a] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-800">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#00ff88]" /> Upgrade Your Plan
            </h2>
            <p className="text-sm text-gray-400 mt-1">Pro $29/mo • Elite $79/mo • Annual discounts available</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-2" data-testid="close-subscription-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-4">
          {plans.map(plan => (
            <div key={plan.id} className={`subscription-card relative ${plan.popular ? 'featured' : ''}`} data-testid={`plan-${plan.id}`}>
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-[#00ff88]">${plan.price}</span>
                  <span className="text-sm text-gray-400">/month</span>
                </div>
              </div>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-[#00ff88] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading === plan.id || currentTier === plan.id}
                className={`w-full py-2.5 rounded-lg font-bold text-sm transition-colors ${
                  currentTier === plan.id
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : plan.popular
                    ? 'bg-[#00ff88] text-black hover:bg-[#00cc6a]'
                    : 'bg-[#00ff88]/20 text-[#00ff88] hover:bg-[#00ff88]/30'
                }`}
                data-testid={`subscribe-${plan.id}-btn`}
              >
                {loading === plan.id ? (
                  <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                ) : currentTier === plan.id ? (
                  'Current Plan'
                ) : (
                  'Subscribe'
                )}
              </button>
              {plan.finePrint && (
                <p className="text-[10px] text-gray-500 mt-2 text-center">{plan.finePrint}</p>
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-gray-800 text-center">
          <p className="text-xs text-gray-500">Secure payments via Stripe. Cancel anytime.</p>
        </div>
      </div>
    </div>
  );
};

// ─── Small UI Pieces ─────────────────────────────────────────
const RatingBadge = ({ rating }) => {
  const cls = { Strong: 'rating-strong', Good: 'rating-good', Marginal: 'rating-marginal', Unprofitable: 'rating-unprofitable' };
  return <span className={`px-2 py-0.5 rounded text-xs font-bold ${cls[rating] || 'bg-gray-600'}`} data-testid={`rating-${rating?.toLowerCase()}`}>{rating}</span>;
};

const ChainBadge = ({ chain }) => (
  <span className={`chain-badge ${CHAIN_COLORS[chain] || 'bg-gray-700 text-gray-300'}`}>{chain}</span>
);

// ─── Risk Grade Badge (Feature 3) ────────────────────────────
const GRADE_COLORS = {
  A: 'bg-emerald-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', F: 'bg-red-500'
};
const GRADE_TEXT_COLORS = {
  A: 'text-emerald-400', B: 'text-blue-400', C: 'text-yellow-400', D: 'text-orange-400', F: 'text-red-400'
};

const RiskGradeBadge = ({ grade, score, size = 'sm' }) => {
  if (!grade) return null;
  const bgColor = GRADE_COLORS[grade] || 'bg-gray-500';
  const sz = size === 'lg' ? 'w-10 h-10 text-lg' : 'w-7 h-7 text-xs';
  return (
    <div className="relative group" data-testid={`risk-grade-${grade}`}>
      <div className={`${bgColor} ${sz} rounded-full flex items-center justify-center font-bold text-white shadow-md`}>
        {grade}
      </div>
      {score != null && (
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 border border-gray-700 text-[10px] text-gray-300 px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
          Risk Score: {score}/100
        </div>
      )}
    </div>
  );
};

// ─── Security Shield Badge (Feature 4) ───────────────────────
const SecurityBadge = ({ security }) => {
  if (!security || security.score == null) {
    return (
      <span className="flex items-center gap-1 text-[10px] text-gray-600" title="Security data unavailable" data-testid="security-badge">
        <Shield className="w-3 h-3" /> —
      </span>
    );
  }
  const score = security.score;
  const color = score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-yellow-400' : 'text-red-400';
  const hasWarning = security.is_honeypot || security.buy_tax > 5 || security.sell_tax > 5 || security.is_mintable;
  return (
    <span className={`flex items-center gap-1 text-[10px] font-medium ${color}`} title={`Security: ${score}/100`} data-testid="security-badge">
      <Shield className="w-3 h-3" />
      {score}/100
      {hasWarning && <AlertTriangle className="w-3 h-3 text-red-400 ml-0.5" />}
    </span>
  );
};

// ─── Auth Modal (inline, lightweight) ────────────────────────
const AuthModal = ({ onClose }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [refCode, setRefCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill referral code from URL param ?ref=CODE
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) { setRefCode(ref.toUpperCase()); setMode('register'); }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Signed in successfully');
      } else {
        await register(email, password, name, refCode.trim() || undefined);
        toast.success('Account created — 7-day free trial started!');
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="auth-modal">
      <div className="bg-[#1a1a2e] max-w-sm w-full rounded-xl p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">{mode === 'login' ? 'Sign In' : 'Create Account'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <input data-testid="auth-name" value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          )}
          <input data-testid="auth-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email or Username" type="text" required className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          <input data-testid="auth-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" type="password" required className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          {mode === 'register' && (
            <input
              data-testid="auth-referral-code"
              value={refCode}
              onChange={e => setRefCode(e.target.value.toUpperCase())}
              placeholder="Referral code (optional)"
              className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88] font-mono tracking-wider placeholder:font-sans placeholder:tracking-normal"
            />
          )}
          <button data-testid="auth-submit" type="submit" disabled={loading} className="w-full bg-[#00ff88] text-black font-bold py-2.5 rounded-lg disabled:opacity-50">
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Register'}
          </button>
        </form>
        <p className="text-center text-sm text-gray-400 mt-4">
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-[#00ff88] hover:underline" data-testid="auth-toggle">
            {mode === 'login' ? 'Register' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
};

// ─── Share Modal ─────────────────────────────────────────────
const ShareModal = ({ pool, onClose }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/?pool=${encodeURIComponent(pool.pool_id)}`;
  const shareText = `${pool.symbol} on ${pool.protocol} — ${pool.apy_total.toFixed(2)}% APY (${pool.chain}). Analyzed with ProfitSpot.`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener');
  };

  const shareTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="share-modal">
      <div className="bg-[#1a1a2e] max-w-sm w-full rounded-xl p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#00ff88]" />
            <h2 className="text-lg font-bold text-white">Share Pool</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Pool preview */}
        <div className="bg-[#0f0f1a] rounded-lg p-3 mb-5 border border-gray-800">
          <div className="flex items-center justify-between mb-1">
            <span className="font-bold text-white text-sm">{pool.symbol}</span>
            <ChainBadge chain={pool.chain} />
          </div>
          <div className="text-xs text-gray-500">{pool.protocol}</div>
          <div className="text-xl font-bold text-[#00ff88] mt-1">{pool.apy_total.toFixed(2)}% APY</div>
        </div>

        {/* Copy URL */}
        <div className="mb-5">
          <p className="text-xs text-gray-500 mb-2">Shareable link</p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={shareUrl}
              className="flex-1 min-w-0 bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-400 focus:outline-none"
              data-testid="share-url-input"
            />
            <button
              onClick={copyLink}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold transition-all ${
                copied
                  ? 'bg-[#00ff88]/20 text-[#00ff88] border border-[#00ff88]/30'
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
              data-testid="copy-link-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Social share */}
        <p className="text-xs text-gray-500 mb-3">Or share directly</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={shareTwitter}
            className="flex items-center justify-center gap-2 py-2.5 bg-[#1d9bf0]/10 text-[#1d9bf0] border border-[#1d9bf0]/30 rounded-lg text-sm font-medium hover:bg-[#1d9bf0]/20 transition-colors"
            data-testid="share-twitter-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            X / Twitter
          </button>
          <button
            onClick={shareTelegram}
            className="flex items-center justify-center gap-2 py-2.5 bg-[#0088cc]/10 text-[#54a9eb] border border-[#0088cc]/30 rounded-lg text-sm font-medium hover:bg-[#0088cc]/20 transition-colors"
            data-testid="share-telegram-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
            Telegram
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Pool Card (Mobile Feed) ─────────────────────────────────
const PoolCard = ({ pool, onClick, onShare, onSimulate, onILCalc, onAIInsights, onForecast, onSetAlert, watchers, isPremium }) => {
  const isPositiveNet = pool.net_apy_estimate > 0;
  const isHighRiskAPY = pool.apy_total >= 200;
  const isExtremeAPY = pool.apy_total >= 400;
  const cardClass = pool.profitspot_rating === 'Unprofitable' ? 'action-card danger' :
                    pool.profitspot_rating === 'Marginal' ? 'action-card warning' : 'action-card';
  const riskRating = pool.risk_rating || {};
  const secSummary = pool.security_summary;
  return (
    <div className={`${cardClass} p-4 rounded-lg mb-3 cursor-pointer`} onClick={() => onClick(pool)} data-testid={`pool-card-${pool.pool_id}`}>
      {/* High APY Risk Warning */}
      {isHighRiskAPY && (
        <div className={`flex items-center gap-2 px-3 py-2 mb-3 rounded-lg text-xs font-medium ${
          isExtremeAPY
            ? 'bg-red-500/15 border border-red-500/30 text-red-400'
            : 'bg-[#ffa502]/15 border border-[#ffa502]/30 text-[#ffa502]'
        }`}>
          <span>{isExtremeAPY ? '🚨' : '⚠️'}</span>
          <span>
            {isExtremeAPY
              ? 'Extreme APY — likely low liquidity, high rug-pull & IL risk. DYOR before investing.'
              : 'High APY — may indicate low TVL or unsustainable incentives. Verify before investing.'}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <RiskGradeBadge grade={riskRating.grade} score={riskRating.score} />
          <span className="font-bold text-white text-sm">{pool.symbol}</span>
          <ChainBadge chain={pool.chain} />
          {watchers > 0 && (
            <span
              className="flex items-center gap-1 px-1.5 py-0.5 bg-[#ffa502]/10 border border-[#ffa502]/20 text-[#ffa502] text-[10px] rounded-full"
              title={`${watchers} ProfitSpot subscriber${watchers > 1 ? 's' : ''} watching this pool`}
              data-testid={`social-score-badge-${pool.pool_id}`}
            >
              <Users className="w-2.5 h-2.5" />
              {watchers}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SecurityBadge security={secSummary} />
          <RatingBadge rating={pool.profitspot_rating} />
        </div>
      </div>
      <div className="text-xs text-gray-500 mb-3">{pool.protocol}</div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Gross APY</div>
          <div className="text-2xl font-bold text-profit apy-glow">{pool.apy_total.toFixed(2)}%</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Net APY</div>
          <div className={`text-2xl font-bold ${isPositiveNet ? 'text-profit' : 'text-loss'}`}>
            {pool.net_apy_estimate?.toFixed(2) || '—'}%
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800">
        <span className="text-[10px] text-gray-500">TVL: {formatUSD(pool.tvl_usd)}</span>
        <span className="text-[10px] text-gray-500">Time-to-Profit: {pool.break_even_days?.toFixed(0) || '—'}d</span>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onILCalc(pool); }}
            className="flex items-center gap-1 px-2 py-1 rounded text-[#ffa502]/60 hover:text-[#ffa502] hover:bg-[#ffa502]/10 transition-all"
            title="IL Calculator"
            data-testid={`il-calc-pool-btn-${pool.pool_id}`}
          >
            <Calculator className="w-3.5 h-3.5" />
            <span className="text-[10px] hidden sm:inline font-medium">IL</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onSimulate(pool); }}
            className="flex items-center gap-1 px-2 py-1 rounded text-[#00ff88]/60 hover:text-[#00ff88] hover:bg-[#00ff88]/10 transition-all"
            title="Simulate returns"
            data-testid={`simulate-pool-btn-${pool.pool_id}`}
          >
            <FlaskConical className="w-3.5 h-3.5" />
            <span className="text-[10px] hidden sm:inline font-medium">Sim</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onAIInsights(pool); }}
            className="flex items-center gap-1 px-2 py-1 rounded text-[#00d4ff]/60 hover:text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all"
            title="AI Insights"
            data-testid={`ai-insights-btn-${pool.pool_id}`}
          >
            <Brain className="w-3.5 h-3.5" />
            <span className="text-[10px] hidden sm:inline font-medium">AI</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onForecast(pool); }}
            className="flex items-center gap-1 px-2 py-1 rounded text-[#8b5cf6]/60 hover:text-[#8b5cf6] hover:bg-[#8b5cf6]/10 transition-all"
            title="Forecast Dial"
            data-testid={`forecast-btn-${pool.pool_id}`}
          >
            <Gauge className="w-3.5 h-3.5" />
            <span className="text-[10px] hidden sm:inline font-medium">Dial</span>
          </button>
          {isPremium && (
            <button
              onClick={(e) => { e.stopPropagation(); onSetAlert && onSetAlert(pool); }}
              className="flex items-center gap-1 px-2 py-1 rounded text-amber-500/60 hover:text-amber-500 hover:bg-amber-500/10 transition-all"
              title="Set Alert"
              data-testid={`set-alert-btn-${pool.pool_id}`}
            >
              <Bell className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onShare(pool); }}
            className="flex items-center gap-1 px-2 py-1 rounded text-gray-500 hover:text-[#00ff88] hover:bg-[#00ff88]/10 transition-all"
            title="Share this pool"
            data-testid={`share-pool-btn-${pool.pool_id}`}
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
          <ChevronRight className="w-4 h-4 text-gray-600 ml-1" />
        </div>
      </div>
    </div>
  );
};

// ─── AI Assistant Panel (Slide-out Drawer) ────────────────────
const AIAssistantPanel = ({ isOpen, onClose, pool, token, userTier, onUpgrade }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [isGated, setIsGated] = useState(false);
  const [freeQuestionsUsed, setFreeQuestionsUsed] = useState(0);
  const messagesEndRef = useRef(null);
  const prevPoolRef = useRef(null);

  const isFree = !userTier || userTier === 'free';
  const FREE_QUESTION_LIMIT = 1;

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset when pool context changes
  useEffect(() => {
    if (!isOpen) return;
    const newPoolId = pool?.pool_id || null;
    const prevPoolId = prevPoolRef.current;
    if (newPoolId !== prevPoolId) {
      prevPoolRef.current = newPoolId;
      setMessages([]);
      setIsGated(false);
      setInputValue('');
      if (pool) {
        fetchPoolAnalysis();
      } else {
        // General mode welcome
        setMessages([{
          role: 'assistant',
          content: "Hey! I'm ProfitSpot AI — your DeFi intelligence assistant. Ask me anything about yields, chains, risk, or click a pool's 🧠 icon to get pool-specific analysis."
        }]);
      }
    }
  }, [pool, isOpen]);

  // Auto-open welcome when panel opens with no pool
  useEffect(() => {
    if (isOpen && !pool && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hey! I'm ProfitSpot AI — your DeFi intelligence assistant. Ask me anything about yields, chains, risk, or click a pool's 🧠 icon to get pool-specific analysis."
      }]);
    }
  }, [isOpen]);

  const fetchPoolAnalysis = async () => {
    if (!pool) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/pool-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          context_mode: 'pool',
          pool_id: pool.pool_id,
          symbol: pool.symbol,
          protocol: pool.protocol,
          chain: pool.chain,
          apy_total: pool.apy_total,
          tvl_usd: pool.tvl_usd,
          net_apy_estimate: pool.net_apy_estimate,
          il_risk: pool.il_risk,
          stable_coin: pool.stable_coin,
          break_even_days: pool.break_even_days,
        })
      });
      if (res.ok) {
        const data = await res.json();
        setConfigured(data.configured !== false);
        if (data.gated) {
          setIsGated(true);
          setMessages([{ role: 'system', content: data.message || 'AI Pool Insights require a Pro or Elite subscription.' }]);
        } else if (data.insights) {
          const insights = data.insights;
          let content = '';
          if (insights.verdict) content += `**Verdict:** ${insights.verdict}\n\n`;
          if (insights.summary) content += `${insights.summary}\n\n`;
          if (insights.pros?.length) content += `**Strengths:**\n${insights.pros.map(p => `• ${p}`).join('\n')}\n\n`;
          if (insights.cons?.length) content += `**Risks:**\n${insights.cons.map(c => `• ${c}`).join('\n')}`;
          setMessages([{ role: 'assistant', content: content.trim() || 'Analysis complete. Ask me anything about this pool!' }]);
        }
      }
    } catch (e) {
      console.error(e);
      setMessages([{ role: 'assistant', content: "I'm ready to analyze this pool. Ask me anything!" }]);
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || loading || isGated) return;

    // Free tier gating after 1 free question
    if (isFree && freeQuestionsUsed >= FREE_QUESTION_LIMIT) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'You\'ve used your free AI question. Upgrade to Pro for unlimited AI insights!'
      }]);
      setIsGated(true);
      return;
    }

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    // Determine context mode
    const contextMode = pool ? 'pool' : 'general';

    try {
      const body = {
        context_mode: contextMode,
        user_question: userMessage,
      };

      if (pool) {
        body.pool_id = pool.pool_id;
        body.symbol = pool.symbol;
        body.protocol = pool.protocol;
        body.chain = pool.chain;
        body.apy_total = pool.apy_total;
        body.tvl_usd = pool.tvl_usd;
        body.net_apy_estimate = pool.net_apy_estimate;
        body.il_risk = pool.il_risk;
        body.stable_coin = pool.stable_coin;
        body.break_even_days = pool.break_even_days;
      }

      const res = await fetch(`${API_URL}/api/ai/pool-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const data = await res.json();
        if (data.gated) {
          setIsGated(true);
          setMessages(prev => [...prev, { role: 'system', content: data.message }]);
        } else {
          const insights = data.insights;
          let content = '';
          if (insights?.answer) {
            content = insights.answer;
          } else if (insights?.summary) {
            content = insights.summary;
          } else {
            content = "I've analyzed your question. Based on the available data, I recommend reviewing the risks and APY stability before investing.";
          }
          setMessages(prev => [...prev, { role: 'assistant', content }]);
          if (isFree) setFreeQuestionsUsed(prev => prev + 1);
        }
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that request. Please try again." }]);
      }
    } catch (e) {
      console.error(e);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Context-aware quick chips
  const generalChips = [
    'Top yields right now',
    'Safest stablecoin pools',
    'Compare chains',
    "What's trending?",
  ];

  const poolChips = [
    'Is this pool safe?',
    'Compare to alternatives',
    'Risk breakdown',
    'Should I invest $1K?',
  ];

  const quickChips = pool ? poolChips : generalChips;

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed z-[95] bg-[#0f0f1a] border-l border-gray-800 flex flex-col shadow-2xl shadow-[#00d4ff]/5 transition-transform duration-300 ease-out
          lg:right-0 lg:top-0 lg:h-full lg:w-[340px]
          max-lg:bottom-0 max-lg:left-0 max-lg:right-0 max-lg:h-[85vh] max-lg:w-full max-lg:rounded-t-2xl max-lg:border-t max-lg:border-l-0
          ${isOpen ? 'translate-x-0 max-lg:translate-y-0' : 'translate-x-full max-lg:translate-y-full'}
        `}
        data-testid="ai-assistant-panel"
      >
        {/* Mobile drag handle */}
        <div className="lg:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#00d4ff]/10 flex items-center justify-center">
              <Brain className="w-4.5 h-4.5 text-[#00d4ff]" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm flex items-center gap-2">
                ProfitSpot AI
              </h3>
              {pool ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#00d4ff]/10 border border-[#00d4ff]/20 text-[#00d4ff] text-[9px] rounded mt-0.5 font-medium">
                  {pool.symbol} · {pool.chain}
                </span>
              ) : (
                <p className="text-[10px] text-gray-500">General DeFi Intelligence</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="close-ai-panel"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Pool Context Card (when a pool is selected) */}
        {pool && (
          <div className="px-4 pt-3 flex-shrink-0">
            <div className="bg-[#0a0a12] rounded-lg p-2.5 border border-gray-800">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Pool Context</span>
                <span className="text-[#00ff88] font-bold">{pool.apy_total?.toFixed(1)}% APY</span>
              </div>
              <div className="flex items-center justify-between text-[10px] mt-1">
                <span className="text-gray-400">{formatUSD(pool.tvl_usd)} TVL</span>
                <span className={`${pool.il_risk === 'low' ? 'text-[#00ff88]' : pool.il_risk === 'medium' ? 'text-[#ffa502]' : 'text-red-400'}`}>
                  {pool.il_risk || 'Medium'} IL Risk
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {!configured ? (
            <div className="text-center py-8 space-y-3">
              <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center mx-auto">
                <Brain className="w-6 h-6 text-gray-500" />
              </div>
              <p className="text-white font-medium text-sm">AI Not Yet Configured</p>
              <p className="text-xs text-gray-400">
                Add your <code className="text-[#ffa502] text-[10px] bg-gray-800 px-1 py-0.5 rounded">EMERGENT_LLM_KEY</code> to enable AI.
              </p>
            </div>
          ) : isGated ? (
            <div className="space-y-3">
              {messages.map((msg, i) => (
                msg.role === 'system' ? (
                  <div key={i} className="bg-[#ffa502]/10 border border-[#ffa502]/30 rounded-lg p-4 text-center">
                    <Crown className="w-7 h-7 text-[#ffa502] mx-auto mb-2" />
                    <p className="text-xs text-gray-300 leading-relaxed">{msg.content}</p>
                    <button
                      onClick={() => onUpgrade?.()}
                      className="mt-3 px-4 py-2 bg-gradient-to-r from-[#00d4ff] to-[#00ff88] text-black text-xs font-bold rounded-lg hover:opacity-90 transition-opacity"
                    >
                      Upgrade to Pro for Unlimited AI
                    </button>
                  </div>
                ) : (
                  <ChatBubble key={i} msg={msg} />
                )
              ))}
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                <ChatBubble key={i} msg={msg} />
              ))}

              {/* Loading indicator */}
              {loading && messages.length > 0 && (
                <div className="flex justify-start">
                  <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full animate-bounce" />
                      <div className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                      <div className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Initial loading */}
              {loading && messages.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <Brain className="w-7 h-7 text-[#00d4ff] animate-pulse" />
                  <p className="text-xs text-gray-400">{pool ? 'Analyzing pool...' : 'Thinking...'}</p>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Quick Chips */}
        {!isGated && configured && !loading && (
          <div className="px-4 pb-2 flex-shrink-0">
            <div className="flex gap-1.5 overflow-x-auto pb-1.5 scrollbar-hide">
              {quickChips.map((q, i) => (
                <button
                  key={i}
                  onClick={() => { setInputValue(q); }}
                  className="flex-shrink-0 px-2.5 py-1.5 bg-gray-800/80 hover:bg-gray-700 border border-gray-700/50 rounded-full text-[10px] text-gray-300 hover:text-white transition-colors whitespace-nowrap"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        {!isGated && configured && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-800 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={pool ? 'Ask about this pool...' : 'Ask anything about DeFi...'}
                className="flex-1 px-3 py-2.5 bg-gray-800/80 border border-gray-700 rounded-xl text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#00d4ff]/50 transition-colors"
                disabled={loading}
                data-testid="ai-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !inputValue.trim()}
                className="p-2.5 bg-[#00d4ff] hover:bg-[#00d4ff]/80 disabled:bg-gray-700 disabled:text-gray-500 text-black rounded-xl transition-colors flex-shrink-0"
                data-testid="ai-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            {isFree && freeQuestionsUsed === 0 && (
              <p className="text-[9px] text-gray-600 mt-1.5 text-center">1 free question · Upgrade to Pro for unlimited</p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// Chat bubble sub-component
const ChatBubble = ({ msg }) => (
  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div className={`max-w-[90%] rounded-xl p-3 ${
      msg.role === 'user'
        ? 'bg-[#00d4ff]/15 border border-[#00d4ff]/25 text-white'
        : msg.role === 'system'
          ? 'bg-[#ffa502]/10 border border-[#ffa502]/25 text-gray-300'
          : 'bg-gray-800/50 border border-gray-700/50 text-gray-200'
    }`}>
      <div className="text-xs leading-relaxed whitespace-pre-wrap">
        {msg.content.split('**').map((part, j) =>
          j % 2 === 1 ? <strong key={j} className="text-white font-semibold">{part}</strong> : part
        )}
      </div>
    </div>
  </div>
);

// ─── Profit Simulator (Bottom-sheet) ─────────────────────────
const ProfitSimulator = ({ pool, onClose, userTier, onUpgrade, isAuthenticated, onSignIn }) => {
  const [depositAmount, setDepositAmount] = useState(1000);
  const [holdingDays, setHoldingDays] = useState(30);
  // Custom APY: Initialize once from pool data, then user controls it
  const [customApy, setCustomApy] = useState(pool.apy_total);
  const [apyInputValue, setApyInputValue] = useState(pool.apy_total.toFixed(2));
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [securityData, setSecurityData] = useState(null);
  const { token, user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';

  const isPremium = userTier === 'pro' || userTier === 'elite' || userTier === 'unlimited' || isAdmin;
  const isEliteTier = userTier === 'elite' || userTier === 'unlimited' || isAdmin;
  const [pdfLoading, setPdfLoading] = useState(false);

  // Handle APY input changes - only update customApy on blur or Enter
  const handleApyChange = (e) => {
    setApyInputValue(e.target.value);
  };

  const handleApyBlur = () => {
    const parsed = parseFloat(apyInputValue);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 10000) {
      setCustomApy(parsed);
    } else {
      // Reset to current custom APY if invalid
      setApyInputValue(customApy.toFixed(2));
    }
  };

  const handleApyKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApyBlur();
    }
  };

  const resetToPoolApy = () => {
    setCustomApy(pool.apy_total);
    setApyInputValue(pool.apy_total.toFixed(2));
  };

  const handleExportPdf = async () => {
    if (!isEliteTier) {
      toast.info('PDF Report Export is an Elite feature. Upgrade to export professional reports.');
      if (onUpgrade) onUpgrade();
      return;
    }
    setPdfLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pdf/report/${encodeURIComponent(pool.pool_id)}`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `profitspot-report-${pool.pool_id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('PDF report downloaded!');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to generate PDF report');
      }
    } catch (e) {
      console.error('PDF export error:', e);
      toast.error('Failed to download PDF report');
    }
    setPdfLoading(false);
  };

  // Fetch pool security details
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/pools/${encodeURIComponent(pool.pool_id)}/details`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          // security is an object when present, or absent/null when no EVM tokens
          if (data.security && typeof data.security === 'object') {
            setSecurityData(data.security);
          } else if (isPremium) {
            // Authenticated premium user but no security data available for this chain
            setSecurityData(false);
          }
        }
      } catch (e) { /* silent */ }
    })();
  }, [pool.pool_id, token, isPremium]);

  const runSimulation = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/yield-intelligence/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({
          deposit_amount: depositAmount, gross_apy: customApy,
          chain: pool.chain, tvl_usd: pool.tvl_usd,
          is_stablecoin: pool.stable_coin, il_risk_level: pool.il_risk,
          holding_days: holdingDays
        })
      });
      if (res.ok) setSimulation(await res.json());
    } catch (e) { console.error('Simulation error:', e); }
    setLoading(false);
  }, [depositAmount, holdingDays, customApy, pool.chain, pool.tvl_usd, pool.stable_coin, pool.il_risk, token]);

  useEffect(() => { runSimulation(); }, [runSimulation]);

  const sim = simulation?.simulation;
  const chartData = simulation?.break_even_chart;
  const chartBlurred = simulation?.break_even_chart?.blur;

  const isApyModified = Math.abs(customApy - pool.apy_total) > 0.01;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center" data-testid="profit-simulator">
      <div className="bg-[#1a1a2e] w-full md:max-w-lg md:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#1a1a2e] p-4 border-b border-gray-800 flex items-center justify-between z-10">
          <div>
            <h3 className="font-bold text-lg text-white">{pool.symbol}</h3>
            <p className="text-sm text-gray-400">{pool.protocol} on {pool.chain}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* PDF Export Button */}
            <button
              onClick={handleExportPdf}
              disabled={pdfLoading}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                isEliteTier
                  ? 'bg-[#00ff88]/10 border border-[#00ff88]/30 text-[#00ff88] hover:bg-[#00ff88]/20'
                  : 'bg-gray-800/50 border border-gray-700 text-gray-500 cursor-not-allowed'
              }`}
              title={isEliteTier ? 'Export PDF Report' : 'Upgrade to Elite to export reports'}
              data-testid="export-pdf-btn"
            >
              {pdfLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileText className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">{pdfLoading ? 'Generating...' : 'Export PDF'}</span>
              {!isEliteTier && (
                <Crown className="w-3 h-3 text-[#ffa502]" />
              )}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-2" data-testid="close-simulator">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Custom APY Input - What-If Scenario */}
          <div>
            <label className="block text-sm text-gray-400 mb-2 flex items-center justify-between">
              <span>APY (What-If Scenario)</span>
              {isApyModified && (
                <button 
                  onClick={resetToPoolApy}
                  className="text-xs text-[#00d4ff] hover:text-[#00d4ff]/80 transition-colors"
                >
                  Reset to pool APY ({pool.apy_total.toFixed(1)}%)
                </button>
              )}
            </label>
            <div className="flex items-center gap-2">
              <input 
                type="number" 
                step="0.1"
                min="0"
                max="10000"
                value={apyInputValue}
                onChange={handleApyChange}
                onBlur={handleApyBlur}
                onKeyDown={handleApyKeyDown}
                className={`flex-1 px-3 py-2 bg-gray-800 border rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none transition-colors ${
                  isApyModified 
                    ? 'border-[#ffa502]/50 focus:border-[#ffa502]' 
                    : 'border-gray-700 focus:border-[#00ff88]/50'
                }`}
                data-testid="apy-input"
              />
              <span className="text-profit font-bold">%</span>
            </div>
            {isApyModified && (
              <p className="text-[10px] text-[#ffa502] mt-1 flex items-center gap-1">
                <span>⚡</span> Custom APY scenario — Pool's current APY is {pool.apy_total.toFixed(2)}%
              </p>
            )}
          </div>

          {/* Deposit slider */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Deposit Amount (USD)</label>
            <input type="range" min="100" max="100000" step="100" value={depositAmount}
              onChange={e => setDepositAmount(Number(e.target.value))} className="w-full accent-[#00ff88]" />
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-500">$100</span>
              <span className="text-profit font-bold">${depositAmount.toLocaleString()}</span>
              <span className="text-gray-500">$100K</span>
            </div>
          </div>
          {/* Days slider */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Holding Period (Days)</label>
            <input type="range" min="7" max="365" value={holdingDays}
              onChange={e => setHoldingDays(Number(e.target.value))} className="w-full accent-[#00ff88]" />
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-500">7d</span>
              <span className="text-profit font-bold">{holdingDays}d</span>
              <span className="text-gray-500">365d</span>
            </div>
          </div>

          {/* Security Score Section */}
          {securityData && !securityData.blur && (
            <div className="neon-card p-4 rounded-lg" data-testid="security-score-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#00d4ff]" />
                  <span className="text-sm font-bold text-white">Security Score</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                  securityData.score >= 80 ? 'bg-[#00ff88]/15 text-[#00ff88]' :
                  securityData.score >= 50 ? 'bg-[#ffa502]/15 text-[#ffa502]' :
                  'bg-red-500/15 text-red-400'
                }`} data-testid="security-level-badge">{securityData.level || 'Unknown'}</span>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-3xl font-bold text-white" data-testid="security-score-value">
                  {securityData.score != null ? Math.round(securityData.score) : '—'}
                </div>
                <div className="flex-1">
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        securityData.score >= 80 ? 'bg-[#00ff88]' :
                        securityData.score >= 50 ? 'bg-[#ffa502]' : 'bg-red-400'
                      }`}
                      style={{ width: `${Math.min(securityData.score || 0, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                    <span>0</span><span>50</span><span>100</span>
                  </div>
                </div>
              </div>
              {securityData.safe_items?.length > 0 && (
                <div className="space-y-1 mb-2">
                  {securityData.safe_items.slice(0, 2).map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-[#00ff88]">
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              )}
              {securityData.warnings?.length > 0 && (
                <div className="space-y-1">
                  {securityData.warnings.slice(0, 2).map((warn, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-[#ffa502]">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{warn}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {securityData?.blur && (
            <div className="neon-card p-4 rounded-lg" data-testid="security-score-gated">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-bold text-gray-400">Security Score</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-[#ffa502]/20 text-[#ffa502] rounded font-bold ml-auto">PRO</span>
              </div>
              <p className="text-xs text-gray-500">Upgrade to Pro to see GoPlus security scores and risk warnings.</p>
            </div>
          )}

          {/* Show when security data fetch returned but no data available (non-EVM chains) */}
          {securityData === false && (
            <div className="neon-card p-4 rounded-lg opacity-50" data-testid="security-score-unavailable">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Security Score</span>
                <span className="text-[10px] text-gray-700 ml-auto">Not available for this chain</span>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-profit" /></div>
        ) : sim && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="neon-card p-4 rounded-lg">
                <div className="text-[10px] text-gray-500 uppercase">Gross APY</div>
                <div className="text-2xl font-bold text-white">{sim.gross_apy.toFixed(2)}%</div>
              </div>
              <div className="neon-card p-4 rounded-lg">
                <div className="text-[10px] text-gray-500 uppercase">Net APY</div>
                <div className={`text-2xl font-bold ${sim.net_apy > 0 ? 'text-profit' : 'text-loss'}`}>{sim.net_apy.toFixed(2)}%</div>
              </div>
            </div>

            {/* Break-even Chart Section */}
            <div className="neon-card p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#00ff88]" />
                  <span className="text-sm font-bold text-white">Time to Profit</span>
                </div>
                {!isPremium && (
                  <span className="text-[10px] px-2 py-0.5 bg-[#ffa502]/20 text-[#ffa502] rounded font-bold">PRO</span>
                )}
              </div>

              {/* Only show blur if backend returns blur object OR no chart data */}
              {(chartBlurred || (!chartData || !Array.isArray(chartData))) ? (
                <div className="premium-blur-container" style={{ minHeight: '200px' }}>
                  <div className="premium-blur-content">
                    <BreakEvenChart 
                      data={Array.from({length: 30}, (_, i) => ({
                        day: i + 1,
                        cumulative_yield: (i + 1) * 2,
                        total_costs: 25,
                        break_even_reached: i >= 12
                      }))}
                      crossoverDay={13}
                    />
                  </div>
                  <PremiumUnlockOverlay
                    feature="Time-to-Profit Chart"
                    tier="pro"
                    onUnlock={onUpgrade}
                    onSignIn={onSignIn}
                    isAuthenticated={isAuthenticated}
                  />
                </div>
              ) : (
                <>
                  <BreakEvenChart 
                    data={chartData}
                    crossoverDay={sim.break_even_days}
                  />
                  <p className="text-[10px] text-gray-500 text-center mt-2">
                    Yield overtakes costs at day {sim.break_even_days.toFixed(0)}
                  </p>
                </>
              )}
            </div>

            <div className="neon-card p-4 rounded-lg">
              <div className="text-sm font-bold text-white mb-3">Cost Breakdown</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Gas (Entry + Exit)</span><span className="text-loss">-${sim.total_gas_cost.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Slippage ({sim.estimated_slippage}%)</span><span className="text-loss">-${sim.slippage_cost.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Est. IL ({sim.il_risk_level})</span><span className="text-loss">-${sim.il_cost.toFixed(2)}</span></div>
                <div className="flex justify-between pt-2 border-t border-gray-700 font-bold">
                  <span className="text-white">Total Costs</span>
                  <span className="text-loss">-${(sim.total_gas_cost + sim.slippage_cost + sim.il_cost).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="neon-card p-4 rounded-lg">
              <div className="text-sm font-bold text-white mb-3">Yield Projections</div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-400">Daily</span><div className="text-profit font-bold">${sim.daily_yield.toFixed(2)}</div></div>
                <div><span className="text-gray-400">Weekly</span><div className="text-profit font-bold">${sim.weekly_yield.toFixed(2)}</div></div>
                <div><span className="text-gray-400">Monthly</span><div className="text-profit font-bold">${sim.monthly_yield.toFixed(2)}</div></div>
                <div><span className="text-gray-400">Yearly</span><div className="text-profit font-bold">${sim.yearly_yield.toFixed(2)}</div></div>
              </div>
            </div>

            <div className={`p-4 rounded-lg ${sim.is_profitable_30d ? 'bg-profit/10 border border-profit/30' : 'bg-loss/10 border border-loss/30'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Break-Even Point</div>
                  <div className="text-xl font-bold text-white">{sim.break_even_days.toFixed(0)} days</div>
                </div>
                <RatingBadge rating={sim.profitspot_rating} />
              </div>
            </div>

            {sim.warnings?.length > 0 && (
              <div className="neon-card-warning p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  <span className="text-sm font-bold text-warning">Warnings</span>
                </div>
                <ul className="text-sm text-gray-300 space-y-1">
                  {sim.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                </ul>
              </div>
            )}

            {/* Forecast Dial — Monte Carlo Simulation (Elite Feature) */}
            <ForecastDial
              poolId={pool.pool_id}
              symbol={pool.symbol}
              currentApy={pool.apy_total}
              tvl={pool.tvl_usd}
              ilRisk={pool.il_risk || 'medium'}
              chain={pool.chain}
              protocol={pool.protocol}
              userTier={userTier}
              onUpgrade={onUpgrade}
              apiUrl={API_URL}
              token={token}
              syncedDays={holdingDays}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Filter Panel ────────────────────────────────────────────
const FilterPanel = ({ filters, setFilters, onClose, isFreeUser, onUpgrade }) => {
  const chains = ['ethereum', 'arbitrum', 'polygon', 'optimism', 'base', 'bsc', 'avalanche', 'solana'];
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end justify-center md:items-center" data-testid="filter-panel">
      <div className="bg-[#1a1a2e] w-full md:max-w-md md:rounded-xl rounded-t-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-white">Filters</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">Chains</label>
          <div className="flex flex-wrap gap-2">
            {chains.map(c => (
              <button key={c} onClick={() => {
                const cur = filters.chains || [];
                setFilters({ ...filters, chains: cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c] });
              }} className={`chain-badge ${CHAIN_COLORS[c]} ${filters.chains?.includes(c) ? 'ring-2 ring-profit' : 'opacity-50'}`}>{c}</button>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">Min APY: {filters.minApy}%</label>
          <input type="range" min="0" max="100" value={filters.minApy} onChange={e => setFilters({ ...filters, minApy: Number(e.target.value) })} className="w-full accent-[#00ff88]" />
        </div>
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">Max APY: {filters.maxApy}%</label>
          <input type="range" min="10" max="500" value={filters.maxApy} onChange={e => setFilters({ ...filters, maxApy: Number(e.target.value) })} className="w-full accent-[#00ff88]" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer mb-4">
          <input type="checkbox" checked={filters.stablecoinsOnly} onChange={e => setFilters({ ...filters, stablecoinsOnly: e.target.checked })} className="accent-[#00ff88] w-4 h-4" />
          <span className="text-sm text-gray-300">Stablecoins Only (Lower IL Risk)</span>
        </label>
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">Risk Grade <FeatureInfo featureKey="risk_rating" /></label>
          <div className="flex gap-2">
            {['A', 'B', 'C', 'D', 'F'].map(g => {
              const selected = (filters.grades || []).includes(g);
              return (
                <button key={g} onClick={() => {
                  if (isFreeUser) { toast.info('Risk grade filter is a Pro feature'); onUpgrade && onUpgrade(); return; }
                  const cur = filters.grades || [];
                  setFilters({ ...filters, grades: selected ? cur.filter(x => x !== g) : [...cur, g] });
                }} className={`${GRADE_COLORS[g]} w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm transition-all ${selected ? 'ring-2 ring-white scale-110' : 'opacity-40 hover:opacity-70'}`}>
                  {g}
                </button>
              );
            })}
          </div>
          {isFreeUser && <p className="text-[10px] text-gray-600 mt-1">Pro feature — upgrade to filter by risk grade</p>}
        </div>
        <button onClick={onClose} className="w-full bg-[#00ff88] text-black font-bold py-3 rounded-lg" data-testid="apply-filters-btn">Apply Filters</button>
      </div>
    </div>
  );
};

// ─── IL Curve Mini Chart ─────────────────────────────────────
const ILCurveChart = ({ curvePoints, currentPct, currentIL }) => {
  const W = 300, H = 72;
  const pad = { t: 6, r: 6, b: 18, l: 28 };
  const cW = W - pad.l - pad.r, cH = H - pad.t - pad.b;
  const X_MIN = -90, X_MAX = 300, Y_MAX = 50;

  const xS = (p) => pad.l + ((p - X_MIN) / (X_MAX - X_MIN)) * cW;
  const yS = (il) => pad.t + (Math.min(-il, Y_MAX) / Y_MAX) * cH;

  const pts = curvePoints.filter(p => p.pct >= X_MIN && p.pct <= X_MAX);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xS(p.pct).toFixed(1)},${yS(p.il).toFixed(1)}`).join(' ');
  const area = `${line} L${xS(X_MAX).toFixed(1)},${pad.t} L${xS(X_MIN).toFixed(1)},${pad.t}Z`;
  const cX = xS(Math.max(X_MIN, Math.min(X_MAX, currentPct)));
  const cY = yS(currentIL);

  return (
    <div>
      <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">IL Curve</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: '72px' }}>
        <defs>
          <linearGradient id="ilg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffa502" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#ffa502" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <line x1={pad.l} y1={pad.t} x2={pad.l + cW} y2={pad.t} stroke="#1f2937" strokeWidth="0.5" />
        <line x1={pad.l} y1={pad.t + cH / 2} x2={pad.l + cW} y2={pad.t + cH / 2} stroke="#1f2937" strokeWidth="0.5" strokeDasharray="2,2" />
        <line x1={xS(0).toFixed(1)} y1={pad.t} x2={xS(0).toFixed(1)} y2={pad.t + cH} stroke="#374151" strokeWidth="0.5" strokeDasharray="2,2" />
        <path d={area} fill="url(#ilg)" />
        <path d={line} fill="none" stroke="#ffa502" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <line x1={cX.toFixed(1)} y1={pad.t} x2={cX.toFixed(1)} y2={pad.t + cH} stroke="#ffa502" strokeWidth="1" strokeDasharray="2,2" opacity="0.7" />
        <circle cx={cX.toFixed(1)} cy={cY.toFixed(1)} r="3" fill="#ffa502" stroke="#0a0a12" strokeWidth="1.5" />
        <text x={pad.l - 3} y={pad.t + 4} textAnchor="end" fontSize="7" fill="#4b5563">0%</text>
        <text x={pad.l - 3} y={pad.t + cH} textAnchor="end" fontSize="7" fill="#4b5563">-50%</text>
        <text x={pad.l} y={H - 2} fontSize="7" fill="#4b5563">-90%</text>
        <text x={xS(0).toFixed(1)} y={H - 2} fontSize="7" fill="#6b7280" textAnchor="middle">0%</text>
        <text x={pad.l + cW} y={H - 2} textAnchor="end" fontSize="7" fill="#4b5563">+300%</text>
      </svg>
    </div>
  );
};

// ─── Quick IL Calculator Modal ───────────────────────────────
const ILCalculatorModal = ({ pool: initialPool, onClose, userTier, onUpgrade }) => {
  const isFreeUser = !userTier || userTier === 'free';
  const [selectedPool, setSelectedPool] = useState(initialPool?.pool_id ? initialPool : null);
  const [priceChange, setPriceChange] = useState(50);
  const [poolSearch, setPoolSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Fetch pools as user types (debounced)
  useEffect(() => {
    if (!poolSearch || poolSearch.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    const t = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/pools?search=${encodeURIComponent(poolSearch)}&limit=8&sort_by=apy`);
        if (res.ok) {
          const data = await res.json();
          const vol = (data.pools || []).filter(p => !p.stable_coin && p.il_risk !== 'no');
          setSearchResults(vol.length ? vol : (data.pools || []).slice(0, 6));
          setShowResults(true);
        }
      } catch (e) { console.error(e); }
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [poolSearch]);

  const calculateIL = (pct) => {
    const r = 1 + pct / 100;
    if (r <= 0) return -100;
    return ((2 * Math.sqrt(r)) / (1 + r) - 1) * 100;
  };

  const il = calculateIL(priceChange);
  const ilAbs = Math.abs(il);

  const curvePoints = [];
  for (let p = -90; p <= 300; p += 5) curvePoints.push({ pct: p, il: calculateIL(p) });

  const netApy = selectedPool ? selectedPool.apy_total + il : null;
  const paybackDays = selectedPool?.apy_total > 0 && ilAbs > 0 ? ilAbs / (selectedPool.apy_total / 365) : null;

  const refScenarios = [
    { change: 25, il: calculateIL(25) }, { change: 50, il: calculateIL(50) },
    { change: 100, il: calculateIL(100) }, { change: -25, il: calculateIL(-25) },
    { change: -50, il: calculateIL(-50) }, { change: -75, il: calculateIL(-75) },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()} data-testid="il-calculator-modal">
      <div className="bg-[#1a1a2e] max-w-md w-full rounded-xl border border-gray-800 flex flex-col" style={{ maxHeight: '92vh' }}>

        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#ffa502]/10 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-[#ffa502]" />
            </div>
            <div>
              <h3 className="font-bold text-white">IL Calculator</h3>
              <p className="text-xs text-gray-500">Impermanent Loss Simulator</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid="close-il-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {/* Pool Typeahead Search */}
          <div>
            <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 block">Pool to Analyze</label>
            {selectedPool ? (
              <div className="flex items-center gap-3 p-3 bg-[#0a0a12] rounded-lg border border-[#ffa502]/30">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{selectedPool.symbol}</p>
                  <p className="text-xs text-gray-500">{selectedPool.protocol} · {selectedPool.chain} · <span className="text-[#00ff88]">{selectedPool.apy_total?.toFixed(2)}% APY</span></p>
                </div>
                <button onClick={() => setSelectedPool(null)}
                  className="text-gray-600 hover:text-white flex-shrink-0 transition-colors p-1" data-testid="il-clear-pool-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : isFreeUser ? (
              /* Free tier: locked search with upgrade CTA */
              <div className="relative">
                <div
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800/30 border border-gray-700/50 rounded-lg text-sm flex items-center gap-2 cursor-pointer hover:border-[#00ff88]/30 transition-colors"
                  onClick={() => { onUpgrade && onUpgrade(); toast.info('Upgrade to Pro to search 6,000+ pools'); }}
                  data-testid="il-pool-search-locked"
                >
                  <Search className="absolute left-3 w-4 h-4 text-gray-600 pointer-events-none" />
                  <span className="text-gray-500">Search pools by token, protocol or chain...</span>
                  <span className="ml-auto text-[10px] text-[#00ff88]/70 bg-[#00ff88]/10 px-2 py-0.5 rounded border border-[#00ff88]/20 flex-shrink-0 font-bold">Pro</span>
                </div>
                <p className="text-[10px] text-[#00ff88]/50 mt-1.5 flex items-center gap-1">
                  <Lock className="w-2.5 h-2.5 inline" />
                  Upgrade to search all 6,000+ pools and compare IL against live APY
                </p>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  value={poolSearch}
                  onChange={e => setPoolSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setShowResults(false), 150)}
                  onFocus={() => searchResults.length > 0 && setShowResults(true)}
                  placeholder="Search by token, protocol or chain..."
                  className="w-full pl-10 pr-9 py-2.5 bg-[#0a0a12] border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffa502]/60 focus:ring-1 focus:ring-[#ffa502]/20 transition-colors"
                  data-testid="il-pool-search-input"
                />
                {isSearching
                  ? <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />
                  : poolSearch && (
                    <button onClick={() => { setPoolSearch(''); setShowResults(false); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                      <X className="w-4 h-4" />
                    </button>
                  )
                }
                {showResults && (
                  <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-[#1a1a2e] border border-gray-700 rounded-lg overflow-hidden shadow-2xl max-h-52 overflow-y-auto">
                    {searchResults.length > 0 ? searchResults.map(p => (
                      <button key={p.pool_id}
                        onClick={() => { setSelectedPool(p); setPoolSearch(''); setShowResults(false); }}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-800/60 transition-colors border-b border-gray-800/50 last:border-0 text-left"
                        data-testid={`il-pool-option-${p.pool_id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-white truncate">{p.symbol}</p>
                          <p className="text-xs text-gray-500">{p.protocol} · {p.chain}</p>
                        </div>
                        <div className="text-right ml-3 flex-shrink-0">
                          <p className="text-sm font-bold text-[#00ff88]">{p.apy_total?.toFixed(1)}%</p>
                          <p className={`text-[10px] ${p.il_risk === 'high' ? 'text-red-400' : p.il_risk === 'medium' ? 'text-[#ffa502]' : 'text-gray-500'}`}>
                            IL: {p.il_risk || '—'}
                          </p>
                        </div>
                      </button>
                    )) : (
                      <p className="px-4 py-3 text-sm text-gray-500 text-center">No pools found — try a different term</p>
                    )}
                  </div>
                )}
              </div>
            )}
            {!selectedPool && !poolSearch && !isFreeUser && (
              <p className="text-[10px] text-gray-600 mt-1.5">Optional — search to compare IL against a pool's live APY</p>
            )}
          </div>

          {/* Price Divergence Slider */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm text-gray-400">Token B Price Change</label>
              <span className={`text-sm font-bold tabular-nums ${priceChange >= 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>
                {priceChange > 0 ? '+' : ''}{priceChange}%
              </span>
            </div>
            <input type="range" min="-90" max="300" step="5" value={priceChange}
              onChange={e => setPriceChange(Number(e.target.value))}
              className="w-full accent-[#ffa502]" data-testid="il-price-slider" />
            <div className="flex justify-between text-[10px] text-gray-600 mt-1">
              <span>-90%</span><span className="text-gray-500">No Change</span><span>+300%</span>
            </div>
          </div>

          {/* IL Result + Curve */}
          <div className="bg-[#0a0a12] rounded-xl p-4 border border-gray-800/60">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Impermanent Loss</p>
                <p className={`text-4xl font-bold tabular-nums mt-1 ${ilAbs > 15 ? 'text-red-400' : ilAbs > 5 ? 'text-[#ffa502]' : 'text-[#00ff88]'}`}>
                  {il.toFixed(2)}%
                </p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-semibold mt-1 ${
                ilAbs < 1 ? 'bg-[#00ff88]/15 text-[#00ff88]' :
                ilAbs < 5 ? 'bg-[#00ff88]/10 text-[#00ff88]' :
                ilAbs < 15 ? 'bg-[#ffa502]/15 text-[#ffa502]' : 'bg-red-500/15 text-red-400'
              }`}>
                {ilAbs < 1 ? 'Negligible' : ilAbs < 5 ? 'Low Risk' : ilAbs < 15 ? 'Moderate' : 'High Risk'}
              </span>
            </div>
            <ILCurveChart curvePoints={curvePoints} currentPct={priceChange} currentIL={il} />
          </div>

          {/* Pool APY Analysis */}
          {selectedPool && (
            <div className="bg-[#0a0a12] rounded-xl p-4 border border-gray-800/60 space-y-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Pool Analysis — {selectedPool.symbol}</p>
              <div className="space-y-2.5">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Pool APY</span>
                    <span className="text-[#00ff88] font-bold">{selectedPool.apy_total.toFixed(2)}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-[#00ff88] rounded-full" style={{ width: `${Math.min(selectedPool.apy_total / 2, 100)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">IL at {priceChange > 0 ? '+' : ''}{priceChange}%</span>
                    <span className={`font-bold ${ilAbs > 15 ? 'text-red-400' : 'text-[#ffa502]'}`}>{il.toFixed(2)}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${ilAbs > 15 ? 'bg-red-400' : 'bg-[#ffa502]'}`}
                      style={{ width: `${Math.min(ilAbs / 2, 100)}%` }} />
                  </div>
                </div>
              </div>
              <div className={`p-3 rounded-lg border ${netApy > 0 ? 'border-[#00ff88]/25 bg-[#00ff88]/5' : 'border-red-500/25 bg-red-500/5'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-gray-500">Net Annual Return (APY − IL)</p>
                    <p className={`text-2xl font-bold tabular-nums mt-0.5 ${netApy > 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>
                      {netApy.toFixed(2)}%
                    </p>
                  </div>
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-lg ${netApy > 0 ? 'bg-[#00ff88]/20 text-[#00ff88]' : 'bg-red-500/20 text-red-400'}`}>
                    {netApy > 0 ? 'PROFITABLE' : 'NET LOSS'}
                  </span>
                </div>
              </div>
              {paybackDays !== null && paybackDays < 3650 && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-800">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs text-gray-400">Days to recover IL via APY</span>
                  </div>
                  <span className={`text-sm font-bold ${paybackDays < 30 ? 'text-[#00ff88]' : paybackDays < 90 ? 'text-[#ffa502]' : 'text-red-400'}`}>
                    {paybackDays < 365 ? `${Math.round(paybackDays)}d` : `${(paybackDays / 365).toFixed(1)}yr`}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Quick Reference — clickable */}
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">
              Quick Reference <span className="text-gray-600 normal-case">(click to apply)</span>
            </p>
            <div className="grid grid-cols-3 gap-2">
              {refScenarios.map((row, i) => (
                <button key={i} onClick={() => setPriceChange(row.change)}
                  className={`rounded-lg p-2.5 text-center transition-all border ${priceChange === row.change ? 'bg-gray-800/80 border-[#ffa502]/40' : 'bg-[#0a0a12] border-gray-800 hover:bg-gray-800/50'}`}
                  data-testid={`il-ref-${row.change}`}>
                  <span className={`block text-xs font-semibold ${row.change >= 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>
                    {row.change > 0 ? '+' : ''}{row.change}%
                  </span>
                  <span className="block text-xs font-bold text-gray-300 mt-0.5">{Math.abs(row.il).toFixed(1)}% IL</span>
                </button>
              ))}
            </div>
          </div>

          {/* Formula */}
          <div className="bg-gray-800/20 rounded-lg p-3 border border-gray-800/50">
            <p className="text-[10px] text-gray-600 font-mono text-center">
              IL = (2√r / (1+r)) − 1 &nbsp;·&nbsp; r = price ratio (Token B / Token A)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Risk Acknowledgment Modal ───────────────────────────────
const RiskAcknowledgmentModal = ({ onAccept }) => (
  <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="risk-modal">
    <div className="bg-[#1a1a2e] max-w-lg rounded-xl p-6 border border-warning/30 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-8 h-8 text-warning flex-shrink-0" />
        <h2 className="text-xl font-bold text-white">Before You Explore...</h2>
      </div>
      <div className="space-y-4 text-sm text-gray-300">
        <p><strong className="text-warning">High yields often come with high risks.</strong> Our Safety Scores are generated by AI and third-party audits, but they are not a guarantee of safety.</p>
        
        <div className="neon-card-warning p-3 rounded-lg">
          <p className="font-bold text-warning mb-1">Gas Matters</p>
          <p className="text-gray-400">Small deposits can be eaten by gas fees. Use our Simulator to check your time-to-profit.</p>
        </div>
        
        <div className="neon-card-warning p-3 rounded-lg">
          <p className="font-bold text-warning mb-1">Price Shifts & Impermanent Loss</p>
          <p className="text-gray-400">High APY in a falling asset can still result in a net loss. Liquidity pools are subject to impermanent loss when token prices diverge.</p>
        </div>

        <div className="neon-card-warning p-3 rounded-lg">
          <p className="font-bold text-warning mb-1">Smart Contract Risk</p>
          <p className="text-gray-400">DeFi protocols can be exploited or hacked. Even audited contracts carry risk. Never invest more than you can afford to lose.</p>
        </div>

        <div className="neon-card-warning p-3 rounded-lg">
          <p className="font-bold text-warning mb-1">Not Financial Advice</p>
          <p className="text-gray-400">ProfitSpot provides data and tools, not investment recommendations. Always do your own research (DYOR) before making any financial decisions.</p>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <p className="text-xs text-gray-400 leading-relaxed">
            By proceeding, you acknowledge that cryptocurrency investments are highly volatile and risky. 
            ProfitSpot is a non-custodial intelligence tool — we never hold your funds or execute transactions on your behalf.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        <button onClick={onAccept} className="w-full bg-[#00ff88] text-black font-bold py-3 rounded-lg flex items-center justify-center gap-2" data-testid="accept-risk-btn">
          <Shield className="w-5 h-5" /> I Understand & I'm Ready to Explore
        </button>
        <a 
          href="/disclaimer" 
          className="w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors"
          data-testid="full-disclaimer-link"
        >
          Read Full Risk Disclaimer
        </a>
      </div>
    </div>
  </div>
);

// ─── Market Stats Ticker ─────────────────────────────────────
const MarketStats = ({ stats }) => (
  <div className="bg-[#0f0f1a] border-b border-gray-800 px-4 py-2.5">
    <div className="flex items-center justify-between text-xs overflow-x-auto gap-6">
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        <DollarSign className="w-3.5 h-3.5 text-profit" />
        <span className="text-gray-500">TVL:</span>
        <span className="text-white font-bold">{stats?.total_tvl_formatted || '—'}</span>
      </div>
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        <TrendingUp className="w-3.5 h-3.5 text-profit" />
        <span className="text-gray-500">Avg APY:</span>
        <span className="text-profit font-bold">{stats?.average_apy?.toFixed(1) || '—'}%</span>
      </div>
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        <Zap className="w-3.5 h-3.5 text-warning" />
        <span className="text-gray-500">Pools:</span>
        <span className="text-white font-bold">{stats?.total_pools?.toLocaleString() || '—'}</span>
      </div>
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        <Users className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-gray-500">Chains:</span>
        <span className="text-white font-bold">{stats?.chains_covered || '—'}</span>
      </div>
    </div>
  </div>
);

// ─── Gas Costs Panel ─────────────────────────────────────────
const GasCostsPanel = () => {
  const [gasCosts, setGasCosts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/gas-costs`);
        if (res.ok) setGasCosts(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-profit" /></div>;
  if (!gasCosts) return <p className="text-center text-gray-500 py-8">Unable to fetch gas data</p>;

  return (
    <div className="space-y-3" data-testid="gas-costs-panel">
      <p className="text-xs text-gray-500 mb-3">Live gas costs for standard deposit + withdraw transactions</p>
      {Object.entries(gasCosts.gas_costs).map(([chain, data]) => (
        <div key={chain} className="neon-card p-4 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ChainBadge chain={chain} />
            {data.is_live && <span className="w-2 h-2 rounded-full bg-profit animate-pulse" title="Live"></span>}
          </div>
          <div className="text-right">
            <div className="text-sm font-bold text-white">${data.total_usd < 0.01 ? data.total_usd.toFixed(4) : data.total_usd.toFixed(2)}</div>
            <div className="text-[10px] text-gray-500">
              {data.gas_gwei ? `${data.gas_gwei.toFixed(2)} Gwei` : 'Est.'} • {data.is_live ? 'Live' : 'Fallback'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};


// ─── Whale Intelligence Panel ────────────────────────────────
const WhalePanel = ({ isAuthenticated, onSignIn, userTier, onUpgrade }) => {
  const [whaleData, setWhaleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token, user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';

  const isPremium = userTier === 'pro' || userTier === 'unlimited' || userTier === 'elite' || isAdmin;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/whales`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) setWhaleData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [token, userTier]); // Refetch when tier changes

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-profit" /></div>;

  // Premium gating with blur effect - only check backend response
  if (whaleData?.blur) {
    return (
      <div className="premium-blur-container min-h-[400px]" data-testid="whale-gated">
        {/* Blurred mock content */}
        <div className="premium-blur-content space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="neon-card p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-20 h-4 bg-gray-700 rounded"></div>
                  <div className="w-12 h-4 bg-gray-800 rounded"></div>
                </div>
                <div className="w-16 h-4 bg-[#00ff88]/20 rounded"></div>
              </div>
              <div className="flex items-center justify-between">
                <div className="w-24 h-3 bg-gray-800 rounded"></div>
                <div className="w-16 h-5 bg-[#00ff88]/10 rounded"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Unlock overlay */}
        <PremiumUnlockOverlay
          feature="Whale Intelligence"
          tier="pro"
          onUnlock={onUpgrade}
          onSignIn={onSignIn}
          isAuthenticated={isAuthenticated}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="whale-panel">
      <p className="text-xs text-gray-500 mb-3">Tracking {whaleData?.tracked_whales} institutional wallets across DeFi protocols</p>
      {whaleData?.trending_protocols?.map((p, i) => (
        <div key={i} className="neon-card p-4 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="font-bold text-white text-sm">{p.name}</span>
            <span className="text-xs text-gray-500">{p.chain}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400"><Users className="inline w-3 h-3 mr-1" />{p.whale_count} whales</span>
            <span className="text-profit font-bold">{formatUSD(p.total_usd)}</span>
          </div>
        </div>
      ))}
      {!whaleData?.is_live && (
        <p className="text-[10px] text-gray-600 text-center mt-2">Demo data • Connect DeBank API for live tracking</p>
      )}
    </div>
  );
};

// ─── Pool Alert Form (Feature 7) ─────────────────────────────
const PoolAlertForm = ({ pool, token, onClose }) => {
  const [alertType, setAlertType] = useState('apy_below');
  const [threshold, setThreshold] = useState('');
  const [creating, setCreating] = useState(false);

  const alertTypes = [
    { value: 'apy_below', label: 'APY drops below', suffix: '%', placeholder: 'e.g. 10' },
    { value: 'apy_above', label: 'APY rises above', suffix: '%', placeholder: 'e.g. 50' },
    { value: 'tvl_drop', label: 'TVL drops by', suffix: '%', placeholder: 'e.g. 30' },
  ];

  const handleCreate = async () => {
    if (!threshold || isNaN(Number(threshold))) { toast.error('Enter a valid threshold'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/alerts/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          pool_id: pool.pool_id,
          pool_name: `${pool.symbol} on ${pool.protocol}`,
          alert_type: alertType,
          threshold: Number(threshold),
          chain: pool.chain,
          snapshot_apy: pool.apy_total,
          snapshot_tvl: pool.tvl_usd,
        }),
      });
      if (res.ok) {
        toast.success('Alert created! You\'ll be notified when triggered.');
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create alert');
      }
    } catch (e) {
      toast.error('Failed to create alert');
    }
    setCreating(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs text-gray-400 mb-2">Alert When</label>
        <div className="grid grid-cols-3 gap-2">
          {alertTypes.map(t => (
            <button key={t.value} onClick={() => setAlertType(t.value)}
              className={`px-2 py-2 rounded-lg text-xs font-medium text-center transition-all ${alertType === t.value ? 'bg-amber-500/20 border border-amber-500/50 text-amber-400' : 'bg-gray-800 border border-gray-700 text-gray-400 hover:border-gray-600'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">Threshold</label>
        <div className="flex items-center gap-2">
          <input
            type="number" value={threshold} onChange={e => setThreshold(e.target.value)}
            placeholder={alertTypes.find(t => t.value === alertType)?.placeholder}
            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50"
          />
          <span className="text-xs text-gray-400">%</span>
        </div>
      </div>
      <button onClick={handleCreate} disabled={creating}
        className="w-full py-2.5 bg-amber-500 text-black font-bold rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50">
        {creating ? 'Creating...' : 'Create Alert'}
      </button>
    </div>
  );
};

// ─── Disclaimer Footer ───────────────────────────────────────
const DisclaimerFooter = () => (
  <div className="bg-[#0a0a12] border-t border-gray-800 px-4 py-3 text-center">
    <p className="text-[10px] text-gray-600 leading-relaxed max-w-3xl mx-auto">
      <strong>Disclaimer:</strong> ProfitSpot is an automated intelligence and data visualization tool.
      We are not a financial advisor, broker, or custodian. All "Net APY" and "Profit Simulations" are
      estimates based on real-time on-chain data and do not guarantee future results. DeFi involves high risk,
      including smart contract failure and permanent loss of funds. Invest only what you can afford to lose.
    </p>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function YieldExplorer() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, logout, refreshUser } = useAuth();
  const [pools, setPools] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPool, setSelectedPool] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showILCalculator, setShowILCalculator] = useState(null); // Pool for IL calc
  const [filters, setFilters] = useState({ chains: [], minApy: 0, maxApy: 500, stablecoinsOnly: false, grades: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('risk');
  const [totalMatching, setTotalMatching] = useState(0);
  const [sharePool, setSharePool] = useState(null);
  const [showAIPanel, setShowAIPanel] = useState(false); // AI panel open/closed
  const [aiContextPool, setAIContextPool] = useState(null); // Pool for AI context (null = general mode)
  const [trendingPools, setTrendingPools] = useState([]); // Community watching
  const [paymentSuccessData, setPaymentSuccessData] = useState(null); // Post-payment success modal
  const [chainLeaderboard, setChainLeaderboard] = useState([]); // Feature 2
  const [btcDefiData, setBtcDefiData] = useState(null); // Feature 6
  const [alertPool, setAlertPool] = useState(null); // Feature 7 — pool for alert modal

  // Navigate to Simulator with pool data pre-filled — paid users only
  const handleSimulatePool = (pool) => {
    if (isFree) {
      setShowSubscriptionModal(true);
      toast.info('Simulator Labs is a Pro feature — upgrade to simulate this pool');
      return;
    }
    const params = new URLSearchParams({
      symbol: pool.symbol,
      protocol: pool.protocol,
      chain: pool.chain,
      apy: pool.apy_total.toFixed(2),
      tvl: pool.tvl_usd,
      stablecoin: pool.stable_coin ? '1' : '0',
      ilRisk: pool.il_risk || 'unknown',
    });
    navigate(`/simulator?${params.toString()}`);
  };

  // First-time risk acknowledgment
  useEffect(() => {
    if (!localStorage.getItem('profitspot_risk_acknowledged')) setShowRiskModal(true);
  }, []);

  // Fetch trending pools (community social signal)
  useEffect(() => {
    fetch(`${API_URL}/api/alerts/trending-pools`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.trending?.length) setTrendingPools(data.trending); })
      .catch((e) => console.warn('Failed to load trending pools:', e));
  }, []);

  // Fetch chain leaderboard (Feature 2)
  useEffect(() => {
    fetch(`${API_URL}/api/yield-intelligence/chain-leaderboard`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.chains) setChainLeaderboard(data.chains.slice(0, 15)); })
      .catch((e) => console.warn('Chain leaderboard fetch failed:', e));
  }, []);

  // Fetch BTC DeFi data (Feature 6)
  useEffect(() => {
    fetch(`${API_URL}/api/yield-intelligence/btc-defi`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBtcDefiData(data); })
      .catch((e) => console.warn('BTC DeFi fetch failed:', e));
  }, []);

  // Social score: match pool symbol+protocol against trending pool names
  const getWatchers = useCallback((pool) => {
    if (!trendingPools.length) return 0;
    const sym = (pool.symbol || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    // Normalize hyphens → spaces so "uniswap-v3" matches "Uniswap V3"
    const proto = (pool.protocol || '').toLowerCase().replace(/-/g, ' ');
    for (const t of trendingPools) {
      const tl = t.pool.toLowerCase();
      const tlNorm = tl.replace(/[^a-z0-9]/g, '');
      if (tlNorm.includes(sym) && (tl.includes(proto.split(' ')[0]) || proto.split(' ').some(w => tl.includes(w)))) {
        return t.watchers;
      }
    }
    return 0;
  }, [trendingPools]);

  // Handle payment success from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const paymentSuccess = params.get('payment_success');
    const paymentCancelled = params.get('payment_cancelled');

    if (paymentCancelled) {
      toast.info('Payment cancelled. You can try again anytime.');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (paymentSuccess && sessionId) {
      // Poll for payment status and show success modal
      const pollPaymentStatus = async (attempts = 0) => {
        if (attempts >= 8) {
          toast.error('Payment verification timed out. Please check your account or contact support@profitspot.live.');
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }

        try {
          const res = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
          if (res.ok) {
            const data = await res.json();
            if (data.payment_status === 'paid') {
              const tierName = data.tier_upgraded?.charAt(0).toUpperCase() + data.tier_upgraded?.slice(1) || 'Pro';
              setPaymentSuccessData({ tier: tierName });
              if (refreshUser) refreshUser();
              window.history.replaceState({}, '', window.location.pathname);
              return;
            } else if (data.status === 'expired') {
              toast.error('Payment session expired. Please try again.');
              window.history.replaceState({}, '', window.location.pathname);
              return;
            }
          }
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        } catch (err) {
          console.error('Payment status check failed:', err);
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        }
      };

      toast.info('Verifying your payment...');
      pollPaymentStatus();
    }
  }, [refreshUser]);

  const handleRiskAccept = () => {
    localStorage.setItem('profitspot_risk_acknowledged', 'true');
    setShowRiskModal(false);
  };

  // Fetch stats
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/stats`);
        if (res.ok) { const d = await res.json(); setStats(d.market_overview); }
      } catch (e) { console.error('Stats fetch failed:', e); }
    })();
  }, []);

  // Pools per page for pagination - backend enforces tier limits (free=25, pro=500, elite=unlimited)
  const POOLS_PER_PAGE = 25; // Request 25 at a time, backend will cap based on tier
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch pools (initial or with new filters)
  const fetchPools = useCallback(async () => {
    setLoading(true);
    setOffset(0);
    try {
      const p = new URLSearchParams();
      if (filters.chains.length) p.append('chains', filters.chains.join(','));
      if (filters.minApy > 0) p.append('min_apy', filters.minApy);
      if (filters.maxApy < 500) p.append('max_apy', filters.maxApy);
      if (filters.stablecoinsOnly) p.append('stablecoins_only', 'true');
      if (filters.grades?.length) p.append('grade', filters.grades.join(','));
      if (debouncedSearch) p.append('search', debouncedSearch);
      p.append('sort_by', sortBy);
      p.append('limit', POOLS_PER_PAGE.toString());
      p.append('offset', '0');

      const res = await fetch(`${API_URL}/api/yield-intelligence/pools?${p}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) { 
        const d = await res.json(); 
        setPools(d.pools || []);
        setHasMore(d.has_more || false);
        setTotalMatching(d.total_matching || 0);
      }
    } catch (e) { console.error('Pool fetch failed:', e); }
    setLoading(false);
  }, [filters, token, debouncedSearch, sortBy]);

  useEffect(() => { fetchPools(); }, [fetchPools]);
  // Auto-open AI panel when navigated from sidebar (?ai=1) or just open panel in general mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ai') === '1') {
      setShowAIPanel(true);
      setAIContextPool(pools.length > 0 ? pools[0] : null);
      // Clean up URL param
      const url = new URL(window.location);
      url.searchParams.delete('ai');
      window.history.replaceState({}, '', url);
    }
  }, [pools]);


  // Load more pools
  const handleLoadMore = async () => {
    setLoadingMore(true);
    const newOffset = offset + POOLS_PER_PAGE;
    try {
      const p = new URLSearchParams();
      if (filters.chains.length) p.append('chains', filters.chains.join(','));
      if (filters.minApy > 0) p.append('min_apy', filters.minApy);
      if (filters.maxApy < 500) p.append('max_apy', filters.maxApy);
      if (filters.stablecoinsOnly) p.append('stablecoins_only', 'true');
      if (filters.grades?.length) p.append('grade', filters.grades.join(','));
      if (debouncedSearch) p.append('search', debouncedSearch);
      p.append('sort_by', sortBy);
      p.append('limit', POOLS_PER_PAGE.toString());
      p.append('offset', newOffset.toString());

      const res = await fetch(`${API_URL}/api/yield-intelligence/pools?${p}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) { 
        const d = await res.json(); 
        setPools(prev => [...prev, ...(d.pools || [])]);
        setHasMore(d.has_more || false);
        setOffset(newOffset);
      }
    } catch (e) { console.error('Load more failed:', e); }
    setLoadingMore(false);
  };

  const userTier = user?.subscription?.tier || 'free';
  const isFree = userTier === 'free';
  const isPremium = userTier === 'pro' || userTier === 'elite' || userTier === 'unlimited' || user?.role === 'admin';
  const isElite = userTier === 'elite' || userTier === 'unlimited' || user?.role === 'admin';
  const isFreeUser = !isPremium && !user?.role === 'admin';

  const handleOpenSubscription = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      toast.info('Please sign in first to upgrade your plan');
    } else {
      setShowSubscriptionModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a12] flex flex-col" data-testid="yield-explorer">
      {showRiskModal && <RiskAcknowledgmentModal onAccept={handleRiskAccept} />}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {showSubscriptionModal && <SubscriptionModal onClose={() => setShowSubscriptionModal(false)} currentTier={userTier} />}
      {sharePool && <ShareModal pool={sharePool} onClose={() => setSharePool(null)} />}

      {/* ── Payment Success Overlay ── */}
      {paymentSuccessData && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="payment-success-overlay">
          <div className="bg-[#0f0f1a] border border-[#00ff88]/30 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-[#00ff88]/15 border-2 border-[#00ff88] flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-[#00ff88]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/></svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Welcome to {paymentSuccessData.tier}! 🎉</h2>
            <p className="text-gray-400 text-sm mb-6">Your account has been upgraded. All {paymentSuccessData.tier} features are now unlocked.</p>
            <div className="space-y-2 text-left bg-[#0a0a12] rounded-xl p-4 mb-6">
              {paymentSuccessData.tier === 'Elite' ? (
                <>
                  <p className="text-xs text-gray-400 flex items-center gap-2"><span className="text-[#00ff88]">✓</span> Unlimited yield pools</p>
                  <p className="text-xs text-gray-400 flex items-center gap-2"><span className="text-[#00ff88]">✓</span> Whale Tracker & AI Insights</p>
                  <p className="text-xs text-gray-400 flex items-center gap-2"><span className="text-[#00ff88]">✓</span> Forecast Dial & Advanced Backtesting</p>
                  <p className="text-xs text-gray-400 flex items-center gap-2"><span className="text-[#00ff88]">✓</span> Telegram + unlimited alerts</p>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-400 flex items-center gap-2"><span className="text-[#00ff88]">✓</span> 500 yield pools with security scores</p>
                  <p className="text-xs text-gray-400 flex items-center gap-2"><span className="text-[#00ff88]">✓</span> Full Simulator Labs & Portfolio Tracker</p>
                  <p className="text-xs text-gray-400 flex items-center gap-2"><span className="text-[#00ff88]">✓</span> Time-to-Profit charts & email alerts</p>
                </>
              )}
            </div>
            <button
              onClick={() => { setPaymentSuccessData(null); window.location.reload(); }}
              className="w-full py-3 bg-[#00ff88] text-black font-bold rounded-xl hover:bg-[#00cc6a] transition-all"
              data-testid="payment-success-continue-btn"
            >
              Start Exploring →
            </button>
          </div>
        </div>
      )}

      {/* ── Market Stats Ticker ── */}
      <MarketStats stats={stats} />

      {/* ── Main Content ── */}
      <div className="flex-1 p-4 md:p-6 max-w-5xl mx-auto w-full">
        {/* Pools Content */}
        <>
            {/* Title + Actions */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl md:text-2xl font-bold text-white" data-testid="page-title">Yield Explorer</h2>
                  <FeatureInfo featureKey="yield_explorer" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Real-time DeFi yields across 10+ chains • Net profit calculations
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowILCalculator({})} className="flex items-center gap-1.5 px-3 py-2 bg-[#ffa502]/10 hover:bg-[#ffa502]/20 rounded-lg text-xs text-[#ffa502] transition-colors" data-testid="il-calc-btn">
                  <Calculator className="w-3.5 h-3.5" /><span className="hidden sm:inline">IL Calc</span>
                </button>
                <button onClick={() => setShowFilters(true)} className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-white transition-colors" data-testid="filter-btn">
                  <Filter className="w-3.5 h-3.5" /><span className="hidden sm:inline">Filters</span>
                </button>
                <button onClick={fetchPools} className="flex items-center gap-1.5 px-3 py-2 bg-[#00ff88]/10 hover:bg-[#00ff88]/20 rounded-lg text-xs text-[#00ff88] transition-colors" data-testid="refresh-btn">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /><span className="hidden sm:inline">Refresh</span>
                </button>
              </div>
            </div>

            {/* 🔥 Trending Chains Leaderboard (Feature 2) */}
            {chainLeaderboard.length > 0 && (
              <div className="mb-5" data-testid="trending-chains">
                <div className="flex items-center gap-2 mb-2.5">
                  <Flame className="w-4 h-4 text-[#00ff88]" />
                  <span className="text-xs font-semibold text-[#00ff88] uppercase tracking-wider">Trending Chains</span>
                  <FeatureInfo featureKey="chain_leaderboard" />
                  <span className="text-[10px] text-gray-600">· top yield chains right now</span>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {chainLeaderboard.slice(0, 12).map((c, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setFilters(prev => ({ ...prev, chains: [c.chain.toLowerCase()] }));
                        setSearchQuery('');
                      }}
                      className={`flex-shrink-0 bg-[#141926] border rounded-xl p-3 min-w-[140px] text-left transition-all hover:border-[#00ff88]/40 hover:bg-[#0f0f1a] ${
                        filters.chains.includes(c.chain.toLowerCase()) ? 'border-[#00ff88]/50 ring-1 ring-[#00ff88]/20' : 'border-gray-800'
                      }`}
                      data-testid={`chain-card-${c.chain}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-white truncate">{c.display_name}</span>
                        <div className="flex items-center gap-1">
                          {c.trending && <span className="text-[9px] bg-[#00ff88]/15 text-[#00ff88] px-1.5 py-0.5 rounded-full font-bold">HOT</span>}
                          {c.category === 'BTC' && <span className="text-[9px] bg-orange-500/15 text-orange-400 px-1.5 py-0.5 rounded-full font-bold">BTC</span>}
                          {c.category === 'L2' && <span className="text-[9px] bg-blue-500/15 text-blue-400 px-1 py-0.5 rounded-full">L2</span>}
                        </div>
                      </div>
                      <div className="text-lg font-bold text-[#00ff88]">{c.top_apy > 1000 ? `${(c.top_apy/1000).toFixed(1)}K` : c.top_apy.toFixed(1)}%</div>
                      <div className="text-[10px] text-gray-500">top APY · {c.pool_count} pools</div>
                      <div className="text-[10px] text-gray-600 mt-0.5">TVL ${c.total_tvl >= 1e9 ? `${(c.total_tvl/1e9).toFixed(1)}B` : `${(c.total_tvl/1e6).toFixed(0)}M`}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ₿ Bitcoin DeFi Spotlight (Feature 6) */}
            {btcDefiData && btcDefiData.btc_pools?.length > 0 && (
              <div className="mb-5 bg-[#141926] border border-orange-500/20 rounded-xl p-4" data-testid="btc-defi-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Bitcoin className="w-5 h-5 text-orange-400" />
                    <span className="text-sm font-bold text-orange-400">Bitcoin DeFi</span>
                    <FeatureInfo featureKey="btc_defi" />
                    <span className="text-[10px] text-gray-500">· {btcDefiData.btc_pool_count} pools, ${(btcDefiData.total_btc_tvl / 1e9).toFixed(1)}B TVL</span>
                  </div>
                  <span className="text-[10px] text-gray-600">Avg yield: {btcDefiData.avg_btc_yield > 100 ? `${btcDefiData.avg_btc_yield.toFixed(0)}` : btcDefiData.avg_btc_yield.toFixed(1)}%</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {btcDefiData.btc_pools.slice(0, 6).map((p, i) => (
                    <div key={i} className="bg-[#0f0f1a] border border-gray-800 rounded-lg p-3 hover:border-orange-500/30 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-white truncate mr-2">{p.symbol}</span>
                        <RiskGradeBadge grade={p.risk_rating?.grade} score={p.risk_rating?.score} />
                      </div>
                      <div className="text-xs text-gray-500 mb-1">{p.protocol} · {p.chain}</div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-[#00ff88]">{p.apy > 1000 ? `${(p.apy/1000).toFixed(1)}K` : p.apy.toFixed(1)}% APY</span>
                        <span className="text-[10px] text-gray-500">${p.tvl_usd >= 1e6 ? `${(p.tvl_usd/1e6).toFixed(1)}M` : `${(p.tvl_usd/1e3).toFixed(0)}K`}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trending Pools — Community Social Signal */}
            {trendingPools.length > 0 && (
              <div className="mb-5" data-testid="trending-pools-widget">
                <div className="flex items-center gap-2 mb-2.5">
                  <Flame className="w-4 h-4 text-[#ffa502]" />
                  <span className="text-xs font-semibold text-[#ffa502] uppercase tracking-wider">Community Watching</span>
                  <span className="text-[10px] text-gray-600">· pools your fellow subscribers are tracking</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {trendingPools.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        const pair = item.pool.split(' on ')[0];
                        const tokens = pair.split('-');
                        const GENERIC = ['ETH', 'WETH', 'BNB', 'MATIC', 'AVAX'];
                        const searchTerm = tokens.find(t => !GENERIC.includes(t.toUpperCase())) || pair;
                        setSearchQuery(searchTerm);
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-900/70 border border-gray-800 hover:border-[#ffa502]/40 rounded-lg text-xs text-gray-400 hover:text-white transition-all group"
                      title={`${item.watchers} subscriber${item.watchers > 1 ? 's' : ''} watching${item.apy != null ? ` · APY: ${item.apy}%` : ''}`}
                      data-testid={`trending-pool-${i}`}
                    >
                      <span className="text-[10px] font-bold text-[#ffa502] bg-[#ffa502]/10 px-1.5 py-0.5 rounded-full tabular-nums">
                        {item.watchers}
                      </span>
                      <span className="truncate max-w-[130px]">{item.pool}</span>
                      {item.apy != null && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                          item.apy >= 20 ? 'bg-[#00ff88]/15 text-[#00ff88]' :
                          item.apy >= 8  ? 'bg-[#ffa502]/15 text-[#ffa502]' :
                                           'bg-gray-700 text-gray-400'
                        }`}>
                          {item.apy}%
                          {item.apy_change_7d != null && item.apy_change_7d !== 0 && (
                            <span className={item.apy_change_7d > 0 ? 'text-[#00ff88]' : 'text-red-400'}>
                              {' '}{item.apy_change_7d > 0 ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active filters */}
            {(filters.chains.length > 0 || filters.stablecoinsOnly || filters.minApy > 0 || filters.grades?.length > 0) && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="active-filters">
                {filters.chains.map(c => <span key={c} className={`chain-badge ${CHAIN_COLORS[c]}`}>{c}</span>)}
                {filters.stablecoinsOnly && <span className="px-2 py-1 bg-[#00ff88]/20 text-[#00ff88] text-[10px] rounded">Stablecoins</span>}
                {filters.minApy > 0 && <span className="px-2 py-1 bg-gray-700 text-gray-300 text-[10px] rounded">Min {filters.minApy}%</span>}
                {filters.grades?.map(g => <span key={g} className={`px-2 py-1 text-[10px] rounded text-white font-bold ${GRADE_COLORS[g]}`}>Grade {g}</span>)}
                <button onClick={() => setFilters({ chains: [], minApy: 0, maxApy: 500, stablecoinsOnly: false, grades: [] })} className="px-2 py-1 text-[10px] text-gray-400 hover:text-white">Clear all</button>
              </div>
            )}

            {/* Search & Sort Bar — paid users only */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              {/* Search Input — gated for free tier */}
              {isFree ? (
                <div className="relative flex-1">
                  <div
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-800/30 border border-gray-700/50 rounded-lg text-sm text-gray-600 flex items-center gap-2 cursor-pointer"
                    onClick={() => { setShowSubscriptionModal(true); toast.info('Upgrade to Pro to search 6,000+ pools'); }}
                    data-testid="pool-search-locked"
                  >
                    <Search className="absolute left-3 w-4 h-4 text-gray-600" />
                    <span className="ml-6 text-gray-600">Search all 6,000+ pools...</span>
                    <span className="ml-auto text-[10px] text-[#00ff88]/60 bg-[#00ff88]/10 px-2 py-0.5 rounded border border-[#00ff88]/20 flex-shrink-0">Pro</span>
                  </div>
                </div>
              ) : (
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search pools, protocols, chains..."
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-800/50 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00ff88]/50 focus:ring-1 focus:ring-[#00ff88]/20 transition-colors"
                    data-testid="pool-search-input"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
              
              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none w-full sm:w-44 pl-3 pr-10 py-2.5 bg-gray-800/50 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-[#00ff88]/50 cursor-pointer"
                  data-testid="sort-select"
                >
                  <option value="apy">Highest APY</option>
                  <option value="net_apy">Best Net APY</option>
                  <option value="tvl">Highest TVL</option>
                  <option value="risk">Lowest Risk</option>
                </select>
                <ArrowUpDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>
              <FeatureInfo featureKey="pool_apy" />
            </div>

            {/* Free tier: Top 10 pools banner */}
            {isFree && (
              <div className="mb-4 p-3 bg-[#00ff88]/5 border border-[#00ff88]/20 rounded-xl flex items-center justify-between gap-3" data-testid="free-tier-limit-banner">
                <div className="flex items-center gap-2.5">
                  <Lock className="w-4 h-4 text-[#00ff88]/60 flex-shrink-0" />
                  <p className="text-xs text-gray-400">
                    <span className="text-white font-semibold">Top 25 curated pools</span> shown for free accounts.
                    <span className="text-gray-500 ml-1">Search all {stats?.total_pools?.toLocaleString() || '6,000+'} pools with Pro.</span>
                  </p>
                </div>
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="flex-shrink-0 px-3 py-1 bg-[#00ff88]/15 text-[#00ff88] border border-[#00ff88]/30 rounded-lg text-xs font-bold hover:bg-[#00ff88]/25 transition-all"
                  data-testid="upgrade-from-limit-btn"
                >
                  Upgrade
                </button>
              </div>
            )}

            {/* Search result indicator */}
            {debouncedSearch && (
              <div className="mb-4 text-sm text-gray-400">
                {loading ? (
                  <span>Searching...</span>
                ) : (
                  <span>Found <span className="text-[#00ff88] font-medium">{totalMatching}</span> pools matching "<span className="text-white">{debouncedSearch}</span>"</span>
                )}
              </div>
            )}

            {/* Pool List */}
            {loading ? (
              <div className="flex items-center justify-center py-20"><RefreshCw className="w-8 h-8 animate-spin text-[#00ff88]" /></div>
            ) : pools.length === 0 ? (
              <div className="text-center py-20">
                <Info className="w-12 h-12 mx-auto text-gray-700 mb-4" />
                <p className="text-gray-400 text-sm">No pools match your filters</p>
                <button onClick={() => setFilters({ chains: [], minApy: 0, maxApy: 500, stablecoinsOnly: false, grades: [] })} className="mt-4 text-[#00ff88] text-sm hover:underline" data-testid="clear-filters-btn">Clear filters</button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="pool-list">
                {pools.map(pool => <PoolCard key={pool.pool_id} pool={pool} watchers={getWatchers(pool)} isPremium={isPremium} onClick={setSelectedPool} onShare={setSharePool} onSimulate={handleSimulatePool} onILCalc={(p) => setShowILCalculator(p)} onAIInsights={(p) => { setAIContextPool(p); setShowAIPanel(true); }} onSetAlert={(p) => { if (isPremium) { setAlertPool(p); } else { setShowSubscriptionModal(true); toast.info('Pool alerts are a Pro+ feature'); }}} onForecast={(p) => navigate(`/forecast?pool=${encodeURIComponent(p.pool_id)}&symbol=${encodeURIComponent(p.symbol)}`)} />)}
              </div>
            )}

            {/* Load More / Pool count notice */}
            {pools.length > 0 && (
              <div className="mt-4 p-4 neon-card rounded-lg text-center" data-testid="pool-count-notice">
                <p className="text-sm text-gray-400">
                  {debouncedSearch ? (
                    <>Showing {pools.length} of {totalMatching.toLocaleString()} matching pools</>
                  ) : (
                    <>Showing {pools.length} of <span className="text-[#00ff88]">{totalMatching.toLocaleString()}</span> pools</>
                  )}
                </p>
                {hasMore ? (
                  <button 
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="mt-3 px-6 py-2.5 bg-[#00ff88]/15 text-[#00ff88] rounded-lg text-sm font-bold hover:bg-[#00ff88]/25 transition-colors disabled:opacity-50 border border-[#00ff88]/25 flex items-center gap-2 mx-auto"
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading...</span>
                    ) : (
                      `Load More (+${POOLS_PER_PAGE})`
                    )}
                  </button>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">
                    {debouncedSearch ? 'All matching pools loaded • Try a different search to explore more' : 'Use search to find specific pools from the full catalog'}
                  </p>
                )}
              </div>
            )}
          </>

      </div>

      <DisclaimerFooter />

      {/* Modals */}
      {showFilters && <FilterPanel filters={filters} setFilters={setFilters} onClose={() => setShowFilters(false)} isFreeUser={isFree} onUpgrade={() => setShowSubscriptionModal(true)} />}
      {showILCalculator && <ILCalculatorModal pool={showILCalculator} onClose={() => setShowILCalculator(null)} userTier={userTier} onUpgrade={handleOpenSubscription} />}
      
      {/* AI Assistant Panel (slide-out drawer) */}
      <AIAssistantPanel
        isOpen={showAIPanel}
        onClose={() => setShowAIPanel(false)}
        pool={aiContextPool}
        token={token}
        userTier={userTier}
        onUpgrade={handleOpenSubscription}
      />

      {/* Floating AI FAB Button */}
      {!showAIPanel && (
        <button
          onClick={() => setShowAIPanel(true)}
          className="fixed bottom-6 right-6 z-[80] w-14 h-14 rounded-full bg-[#0f0f1a] border border-[#00d4ff]/30 flex items-center justify-center shadow-lg shadow-[#00d4ff]/20 hover:shadow-[#00d4ff]/40 hover:border-[#00d4ff]/60 transition-all duration-300 group"
          data-testid="ai-fab-button"
          title="Open ProfitSpot AI"
          style={{
            animation: 'ai-fab-pulse 3s ease-in-out infinite',
          }}
        >
          <Brain className="w-6 h-6 text-[#00d4ff] group-hover:scale-110 transition-transform" />
          <div className="absolute inset-0 rounded-full border border-[#00d4ff]/20 animate-ping opacity-20" />
        </button>
      )}
      {/* Pool Alert Modal (Feature 7) */}
      {alertPool && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="pool-alert-modal">
          <div className="bg-[#1a1a2e] rounded-xl max-w-md w-full p-5 border border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" />
                <h3 className="font-bold text-white text-lg">Set Pool Alert</h3>
                <FeatureInfo featureKey="pool_alerts" />
              </div>
              <button onClick={() => setAlertPool(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-400 mb-4">{alertPool.symbol} on {alertPool.protocol} · Current APY: {alertPool.apy_total?.toFixed(2)}%</p>
            <PoolAlertForm pool={alertPool} token={token} onClose={() => setAlertPool(null)} />
          </div>
        </div>
      )}
      {selectedPool && (
        <ProfitSimulator 
          pool={selectedPool} 
          onClose={() => setSelectedPool(null)} 
          userTier={userTier}
          onUpgrade={handleOpenSubscription}
          isAuthenticated={isAuthenticated}
          onSignIn={() => setShowAuthModal(true)}
        />
      )}
    </div>
  );
}
